import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["item"]; // Targets for the menu items
  static values = { index: Number }; // Index of the current item

  connect() {
    this.indexValue = -1;
    this.searchString = "";
    this.keyboardNavigation = false; // Track keyboard navigation state
    this.#updateTabstops();

    document.addEventListener("keydown", (event) => {
      if (document.activeElement.tagName === "INPUT") return;

      this.keyboardNavigation = true;

      if (this.element.open && event.key.length === 1 && /\S/.test(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        this.#handleTextSearch(event.key.toLowerCase());
      }
    });

    this.itemTargets.forEach((item) => {
      item.addEventListener("click", (event) => {
        // Ensure the item maintains focus when clicked
        item.focus();

        // Update the index to match the clicked item
        this.indexValue = this.itemTargets.indexOf(item);
        this.#updateTabstops();

        // If clicking on a label with checkbox/radio, prevent the input from stealing focus
        const input = item.querySelector('input[type="checkbox"], input[type="radio"]');
        if (input && event.target !== input) {
          // Allow the click to propagate to toggle the checkbox, but maintain focus on the label
          setTimeout(() => {
            item.focus();
          }, 0);
        }

        const isNestedDropdownButton = item.hasAttribute("data-dropdown-popover-target");
        if (!isNestedDropdownButton) {
          const parentDropdown = this.element.closest('[data-controller="dropdown-popover"]');
          const parentContextMenu = this.element.closest('[data-controller="context-menu"]');

          let shouldAutoClose = true;

          if (parentDropdown) {
            shouldAutoClose = parentDropdown.getAttribute("data-dropdown-popover-auto-close-value") !== "false";
          } else if (parentContextMenu) {
            shouldAutoClose = parentContextMenu.getAttribute("data-context-menu-auto-close-value") !== "false";
          }

          if (shouldAutoClose) {
            this.element.dispatchEvent(new Event("menu-item-clicked", { bubbles: true }));
          }
        }
      });

      item.addEventListener("mousemove", (event) => {
        // Ignore mousemove events fired when content scrolls under a stationary cursor.
        if (event.movementX === 0 && event.movementY === 0) return;
        this.keyboardNavigation = false;
      });

      item.addEventListener("mouseenter", (event) => {
        if (!this.element.open) return;
        event.preventDefault();

        // Don't interfere with keyboard navigation
        if (this.keyboardNavigation) {
          return;
        }

        // Only handle mouse enter for visible items
        const currentItem = event.currentTarget;
        if (
          currentItem.disabled ||
          currentItem.classList.contains("disabled") ||
          currentItem.classList.contains("hidden") ||
          currentItem.style.display === "none"
        ) {
          return;
        }

        // Check computed styles for actual visibility (handles Tailwind responsive classes)
        const computedStyle = window.getComputedStyle(currentItem);
        if (computedStyle.display === "none" || computedStyle.visibility === "hidden") {
          return;
        }

        // Close any sibling nested dropdowns with a delay
        const siblingDropdowns = this.element.querySelectorAll(
          '[data-controller="dropdown-popover"][data-dropdown-popover-nested-value="true"]'
        );
        siblingDropdowns.forEach((dropdown) => {
          const controller = this.application.getControllerForElementAndIdentifier(dropdown, "dropdown-popover");
          const isHoverEnabled = controller?.hoverValue;
          const isCurrentItemNestedDropdown = event.currentTarget.hasAttribute("data-dropdown-popover-target");

          // Only close if hover is enabled and current item is not a nested dropdown
          if (
            controller &&
            controller.menuTarget.open &&
            !dropdown.contains(event.currentTarget) &&
            isHoverEnabled &&
            !isCurrentItemNestedDropdown
          ) {
            setTimeout(() => {
              controller.close();
            }, 100);
          }
        });

        this.indexValue = this.itemTargets.indexOf(event.currentTarget);
        this.#updateTabstops();
        // Focus without scrolling for mouse hover to prevent aggressive auto-scroll
        this.#focusCurrentItem({ preventScroll: true });
      });

      item.addEventListener("mouseleave", (event) => {
        if (!this.element.open) return;

        // During keyboard navigation, scrolling items under a stationary cursor can trigger mouseleave.
        // Don't let that steal focus back to the button / reset state.
        if (this.keyboardNavigation) {
          return;
        }

        const isContextMenu = this.element.closest('[data-controller="context-menu"]');
        const parentDropdown = this.element.closest('[data-controller="dropdown-popover"]');

        // Find all open nested dropdown controllers within this menu
        const openNestedControllers = Array.from(this.element.querySelectorAll('[data-controller="dropdown-popover"]'))
          .map((dropdown) => this.application.getControllerForElementAndIdentifier(dropdown, "dropdown-popover"))
          .filter((controller) => controller?.menuTarget?.open);

        if (openNestedControllers.length > 0) {
          // Focus the last (deepest) open nested controller's button
          const deepestController = openNestedControllers[openNestedControllers.length - 1];
          const parentMenu = deepestController.buttonTarget.closest('[data-controller="menu"]');
          const parentMenuController = this.application.getControllerForElementAndIdentifier(parentMenu, "menu");

          // Update tabindex state in the parent menu using public method
          const buttonIndex = parentMenuController.itemTargets.indexOf(deepestController.buttonTarget);
          parentMenuController.updateTabstopsWithIndex(buttonIndex);
          deepestController.buttonTarget.focus();
        } else if (this.indexValue === this.itemTargets.indexOf(event.currentTarget)) {
          if (isContextMenu) {
            this.element.focus();
          } else if (parentDropdown) {
            const dropdownController = this.application.getControllerForElementAndIdentifier(
              parentDropdown,
              "dropdown-popover"
            );
            dropdownController.buttonTarget.focus();
          }
          // Remove state attribute when leaving item
          event.currentTarget.removeAttribute("data-state");
          this.indexValue = -1;
          this.#updateTabstops();
        }
      });

      item.addEventListener("keydown", (event) => {
        this.keyboardNavigation = true;
        if (event.key === "Home") {
          event.preventDefault();
          this.selectFirst();
        } else if (event.key === "End") {
          event.preventDefault();
          this.selectLast();
        } else if (event.key === "Enter") {
          const input = item.querySelector('input[type="checkbox"], input[type="radio"]');
          if (input) {
            event.preventDefault();
            input.checked = !input.checked;

            const parentDropdown = this.element.closest('[data-controller="dropdown-popover"]');
            const parentContextMenu = this.element.closest('[data-controller="context-menu"]');

            let shouldAutoClose = true;

            if (parentDropdown) {
              shouldAutoClose = parentDropdown.getAttribute("data-dropdown-popover-auto-close-value") !== "false";
            } else if (parentContextMenu) {
              shouldAutoClose = parentContextMenu.getAttribute("data-context-menu-auto-close-value") !== "false";
            }

            if (shouldAutoClose) {
              this.element.dispatchEvent(new Event("menu-item-clicked"));
            }
          }
        } else if (event.key === "ArrowRight") {
          const isNestedDropdownButton = item.hasAttribute("data-dropdown-popover-target");
          if (isNestedDropdownButton) {
            const dropdownController = this.application.getControllerForElementAndIdentifier(
              item.closest('[data-controller="dropdown-popover"]'),
              "dropdown-popover"
            );
            dropdownController.show();
            // Add state attribute when opening nested dropdown
            if (dropdownController.nestedValue) {
              item.setAttribute("data-state", "open");
            }
          }
        } else if (event.key === "ArrowLeft") {
          const parentDropdown = this.element.closest('[data-dropdown-popover-nested-value="true"]');
          if (parentDropdown) {
            event.stopPropagation();
            const dropdownController = this.application.getControllerForElementAndIdentifier(
              parentDropdown,
              "dropdown-popover"
            );
            // Remove state attribute when closing nested dropdown
            dropdownController.buttonTarget.removeAttribute("data-state");
            dropdownController.close();
            dropdownController.buttonTarget.focus();
          }
        } else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
          this.element.querySelectorAll(".active-menu-path").forEach((el) => {
            el.classList.remove("active-menu-path");
          });

          const isNestedDropdownButton = item.hasAttribute("data-dropdown-popover-target");
          if (isNestedDropdownButton) {
            const dropdownController = this.application.getControllerForElementAndIdentifier(
              item.closest('[data-controller="dropdown-popover"]'),
              "dropdown-popover"
            );

            if (dropdownController?.menuTarget?.open) {
              event.preventDefault();
              event.stopPropagation();
              const childMenuController = this.application.getControllerForElementAndIdentifier(
                dropdownController.menuTarget,
                "menu"
              );

              // Add active path class and state attribute to parent item
              item.classList.add("active-menu-path");
              item.setAttribute("data-state", "open");

              if (event.key === "ArrowDown") {
                childMenuController.selectFirst();
              } else {
                childMenuController.selectLast();
              }
              return;
            }
          }
        }
      });
    });

    this.element.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") {
        const parentDropdown = this.element.closest('[data-dropdown-popover-nested-value="true"]');
        if (parentDropdown) {
          event.stopPropagation();
          const dropdownController = this.application.getControllerForElementAndIdentifier(
            parentDropdown,
            "dropdown-popover"
          );
          // Remove state attribute when closing nested dropdown
          dropdownController.buttonTarget.removeAttribute("data-state");
          dropdownController.close();
          dropdownController.buttonTarget.focus();
        }
      }
    });

    this.element.addEventListener("mouseleave", (event) => {
      if (!this.element.open) return;

      // Same idea as above: ignore scroll-induced mouseleave while keyboard navigating.
      if (this.keyboardNavigation) {
        return;
      }

      // Check all nested dropdown controllers for open state
      const hasOpenNested = Array.from(this.element.querySelectorAll('[data-controller="dropdown-popover"]')).some(
        (dropdown) => {
          const controller = this.application.getControllerForElementAndIdentifier(dropdown, "dropdown-popover");
          return controller?.menuTarget?.open;
        }
      );

      if (!hasOpenNested) {
        const parentDropdown = event.target.closest('[data-controller="dropdown-popover"]');
        if (parentDropdown) {
          const dropdownController = this.application.getControllerForElementAndIdentifier(
            parentDropdown,
            "dropdown-popover"
          );
          if (dropdownController?.buttonTarget) {
            dropdownController.buttonTarget.focus();
          }
        }
      }
    });
  }

  reset() {
    this.indexValue = -1;
    this.searchString = "";
    this.#updateTabstops();
  }

  // Helper method to get only visible and enabled items
  #getVisibleItems() {
    return this.itemTargets.filter((item) => {
      // Check if item is disabled
      if (item.disabled || item.classList.contains("disabled")) {
        return false;
      }

      // Check if item is explicitly hidden
      if (item.classList.contains("hidden")) {
        return false;
      }

      // Check if item has inline style display none
      if (item.style.display === "none") {
        return false;
      }

      // Check computed styles for actual visibility (handles Tailwind responsive classes)
      const computedStyle = window.getComputedStyle(item);
      if (computedStyle.display === "none" || computedStyle.visibility === "hidden") {
        return false;
      }

      return true;
    });
  }

  prev() {
    this.keyboardNavigation = true;
    this.element.querySelectorAll(".active-menu-path").forEach((el) => {
      el.classList.remove("active-menu-path");
      el.removeAttribute("data-state");
    });

    // Get only visible and enabled items
    const visibleItems = this.#getVisibleItems();
    if (visibleItems.length === 0) return;

    const currentVisibleIndex = visibleItems.indexOf(this.itemTargets[this.indexValue]);

    if (currentVisibleIndex === -1) {
      this.indexValue = this.itemTargets.indexOf(visibleItems[visibleItems.length - 1]);
    } else if (currentVisibleIndex > 0) {
      this.indexValue = this.itemTargets.indexOf(visibleItems[currentVisibleIndex - 1]);
    } else {
      this.indexValue = this.itemTargets.indexOf(visibleItems[visibleItems.length - 1]);
    }

    this.#updateTabstops();
    this.#focusCurrentItem();
  }

  next() {
    this.keyboardNavigation = true;
    this.element.querySelectorAll(".active-menu-path").forEach((el) => {
      el.classList.remove("active-menu-path");
      el.removeAttribute("data-state");
    });

    // Get only visible and enabled items
    const visibleItems = this.#getVisibleItems();
    if (visibleItems.length === 0) return;

    const currentVisibleIndex = visibleItems.indexOf(this.itemTargets[this.indexValue]);

    if (currentVisibleIndex === -1) {
      this.indexValue = this.itemTargets.indexOf(visibleItems[0]);
    } else if (currentVisibleIndex < visibleItems.length - 1) {
      this.indexValue = this.itemTargets.indexOf(visibleItems[currentVisibleIndex + 1]);
    } else {
      this.indexValue = this.itemTargets.indexOf(visibleItems[0]);
    }

    this.#updateTabstops();
    this.#focusCurrentItem();
  }

  preventScroll(event) {
    event.preventDefault();
  }

  #updateTabstops() {
    this.itemTargets.forEach((element, index) => {
      const isHighlighted = index === this.indexValue && this.indexValue !== -1;
      element.tabIndex = isHighlighted ? 0 : -1;

      // Add/remove data-highlighted attribute for CSS styling (Base UI pattern)
      if (isHighlighted) {
        element.setAttribute("data-highlighted", "");
      } else {
        element.removeAttribute("data-highlighted");
      }
    });
  }

  #focusCurrentItem(options = {}) {
    this.itemTargets[this.indexValue].focus(options);
  }

  get #lastIndex() {
    return this.itemTargets.length - 1;
  }

  selectFirst() {
    this.keyboardNavigation = true;
    const visibleItems = this.#getVisibleItems();
    if (visibleItems.length === 0) return;

    this.indexValue = this.itemTargets.indexOf(visibleItems[0]);
    this.#updateTabstops();
    this.#focusCurrentItem();
  }

  selectLast() {
    this.keyboardNavigation = true;
    const visibleItems = this.#getVisibleItems();
    if (visibleItems.length === 0) return;

    this.indexValue = this.itemTargets.indexOf(visibleItems[visibleItems.length - 1]);
    this.#updateTabstops();
    this.#focusCurrentItem();
  }

  #handleTextSearch(key) {
    this.keyboardNavigation = true;
    clearTimeout(this.searchTimeout);
    this.searchString += key;

    const searchStr = this.searchString;
    const visibleItems = this.#getVisibleItems();
    const matchedItem = visibleItems.find((item) => {
      const textElement = item.querySelector(".menu-item-text") || item;
      return textElement.textContent.trim().toLowerCase().startsWith(searchStr);
    });

    if (matchedItem) {
      this.indexValue = this.itemTargets.indexOf(matchedItem);
      this.#updateTabstops();
      this.#focusCurrentItem();
    }

    this.searchTimeout = setTimeout(() => {
      this.searchString = "";
    }, 500);
  }

  // Add public method for external access
  updateTabstopsWithIndex(newIndex) {
    this.indexValue = newIndex;
    this.#updateTabstops();
  }
}
